// /var/www/oszczedzam/client/src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, CircularProgress, Alert,
  Grid, Paper, Card, CardContent, Divider, useTheme,
  IconButton, Tooltip
} from '@mui/material';
import { 
  Add as AddIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  FlagOutlined as FlagOutlinedIcon,
  AssessmentOutlined as AssessmentOutlinedIcon,
  AccountBalanceWalletOutlined as WalletOutlinedIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTransactions } from '../hooks/useTransactions';
import BalanceSummary from '../components/dashboard/BalanceSummary';
import ExpenseChart from '../components/dashboard/ExpenseChart';
import CategoryChart from '../components/dashboard/CategoryChart';
import UpcomingTransactions from '../components/dashboard/UpcomingTransactions';

function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser, getUserData } = useAuth();
  const { transactions, loading, error } = useTransactions();
  const [financialSummary, setFinancialSummary] = useState({ income: 0, expenses: 0, balance: 0 });
  const [upcomingTransactions, setUpcomingTransactions] = useState([]);
  const [userData, setUserData] = useState(null);
  
  // Pobierz dane użytkownika
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        const data = await getUserData();
        setUserData(data);
      }
    };
    
    fetchUserData();
  }, [currentUser, getUserData]);
  
  // Funkcja do personalizowanego powitania
  const getGreeting = () => {
    const hour = new Date().getHours();
    let greeting = '';
    
    if (hour < 12) greeting = 'Dzień dobry';
    else if (hour < 18) greeting = 'Witaj';
    else greeting = 'Dobry wieczór';
    
    return `${greeting}, ${userData?.username || 'Użytkowniku'}!`;
  };
  
  // Oblicz podsumowanie finansowe
  useEffect(() => {
    if (transactions.length > 0) {
      // Oblicz przychody, wydatki i saldo
      let income = 0;
      let expenses = 0;
      
      transactions.forEach(transaction => {
        if (transaction.amount > 0) {
          income += transaction.amount;
        } else {
          expenses += Math.abs(transaction.amount);
        }
      });
      
      setFinancialSummary({
        income,
        expenses,
        balance: income - expenses
      });
      
      // Pobierz nadchodzące transakcje
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const upcoming = transactions
        .filter(t => {
          const transactionDate = new Date(t.date);
          return transactionDate >= today;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);
      
      setUpcomingTransactions(upcoming);
    }
  }, [transactions]);

  // Porady finansowe do wyświetlenia
  const financialTips = [
    {
      title: "Reguła 50/30/20",
      description: "Przeznacz 50% dochodów na potrzeby, 30% na zachcianki i 20% na oszczędności.",
      icon: <AccountBalanceIcon />
    },
    {
      title: "Wyzwanie 30 dni",
      description: "Przed większym zakupem, poczekaj 30 dni aby sprawdzić, czy naprawdę go potrzebujesz.",
      icon: <WalletOutlinedIcon />
    },
    {
      title: "Automatyczne oszczędzanie",
      description: "Ustaw automatyczne przelewy na konto oszczędnościowe zaraz po otrzymaniu wypłaty.",
      icon: <TrendingUpIcon />
    },
    {
      title: "Metoda koperty",
      description: "Podziel gotówkę na kategorie wydatków w osobnych kopertach, aby kontrolować budżet.",
      icon: <WalletOutlinedIcon />
    },
    {
      title: "Zasada 24 godzin",
      description: "Przed nieplanowanym zakupem odczekaj 24 godziny, aby uniknąć impulsywnych decyzji.",
      icon: <TrendingDownIcon />
    }
  ];

  // Wybierz losową poradę
  const randomTip = financialTips[Math.floor(Math.random() * financialTips.length)];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {getGreeting()}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {new Date().toLocaleDateString('pl-PL', { dateStyle: 'full' })}
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => navigate('/transactions')}
          sx={{ px: 3 }}
        >
          Nowa transakcja
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Szybkie akcje */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Paper 
            elevation={0}
            sx={{
              textAlign: 'center',
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: '1px solid #eaeaea',
              height: '100%',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                borderColor: theme.palette.primary.main
              }
            }}
            onClick={() => navigate('/transactions')}
          >
            <Box 
              sx={{ 
                color: 'white', 
                bgcolor: theme.palette.primary.main, 
                borderRadius: '50%', 
                width: 56, 
                height: 56, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 12px'
              }}
            >
              <AddCircleOutlineIcon fontSize="large" />
            </Box>
            <Typography variant="subtitle2">Dodaj transakcję</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper 
            elevation={0}
            sx={{
              textAlign: 'center',
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: '1px solid #eaeaea',
              height: '100%',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                borderColor: theme.palette.secondary.main
              }
            }}
            onClick={() => navigate('/budgets')}
          >
            <Box 
              sx={{ 
                color: 'white', 
                bgcolor: theme.palette.secondary.main, 
                borderRadius: '50%', 
                width: 56, 
                height: 56, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 12px'
              }}
            >
              <WalletOutlinedIcon fontSize="large" />
            </Box>
            <Typography variant="subtitle2">Ustaw budżet</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper 
            elevation={0}
            sx={{
              textAlign: 'center',
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: '1px solid #eaeaea',
              height: '100%',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                borderColor: theme.palette.warning.main
              }
            }}
            onClick={() => navigate('/goals')}
          >
            <Box 
              sx={{ 
                color: 'white', 
                bgcolor: theme.palette.warning.main, 
                borderRadius: '50%', 
                width: 56, 
                height: 56, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 12px'
              }}
            >
              <FlagOutlinedIcon fontSize="large" />
            </Box>
            <Typography variant="subtitle2">Dodaj cel</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper 
            elevation={0}
            sx={{
              textAlign: 'center',
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: '1px solid #eaeaea',
              height: '100%',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                borderColor: theme.palette.info.main
              }
            }}
            onClick={() => navigate('/reports')}
          >
            <Box 
              sx={{ 
                color: 'white', 
                bgcolor: theme.palette.info.main, 
                borderRadius: '50%', 
                width: 56, 
                height: 56, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 12px'
              }}
            >
              <AssessmentOutlinedIcon fontSize="large" />
            </Box>
            <Typography variant="subtitle2">Pokaż raporty</Typography>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Porada finansowa */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 2,
          backgroundColor: 'rgba(76, 175, 80, 0.08)', 
          borderLeft: '4px solid',
          borderColor: 'primary.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box 
            sx={{ 
              backgroundColor: 'primary.main', 
              color: 'white',
              borderRadius: '50%', 
              p: 1,
              mr: 2,
              display: 'flex'
            }}
          >
            <LightbulbOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {randomTip.title}
              </Typography>
              <Tooltip title="Porada finansowa">
                <IconButton size="small">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2">
              {randomTip.description}
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Bilans finansowy */}
          <BalanceSummary 
            income={financialSummary.income}
            expenses={financialSummary.expenses}
            balance={financialSummary.balance}
          />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* Wykres przychodów i wydatków */}
              <ExpenseChart transactions={transactions} />
            </Grid>
            <Grid item xs={12} md={4}>
              {/* Wykres kategorii */}
              <CategoryChart transactions={transactions} />
            </Grid>
          </Grid>
          
          {/* Nadchodzące transakcje */}
          <UpcomingTransactions transactions={upcomingTransactions} />
        </>
      )}
    </Box>
  );
}

export default Dashboard;